import { useEffect, useState } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertCircle, Check, X } from 'lucide-react';
import { subscribe, runSync, retryMutation } from '../services/offlineSync';
import { listMutations, deleteMutation } from '../services/offlineDB';

/**
 * SyncStatusBadge
 * Floating bottom-right badge showing offline/sync status.
 * Click to open the queue modal.
 *
 * - Green cloud + check : online, queue empty
 * - Amber spinner : syncing or pending items
 * - Red cloud-off : offline with pending items
 */
export const SyncStatusBadge = () => {
  const [status, setStatus] = useState({ pending: 0, isSyncing: false, online: navigator.onLine });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => subscribe(setStatus), []);

  useEffect(() => {
    if (open) {
      listMutations().then(setItems);
    }
  }, [open, status.pending]);

  // Hide entirely if everything is fine and queue empty
  if (status.online && status.pending === 0 && !open) {
    return null;
  }

  const Icon = !status.online
    ? CloudOff
    : status.isSyncing
      ? RefreshCw
      : status.pending > 0
        ? AlertCircle
        : Cloud;

  const color = !status.online
    ? 'bg-red-500/15 text-red-400 border-red-500/30'
    : status.pending > 0
      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
      : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-20 right-4 z-40 px-3 py-2 rounded-full border shadow-lg flex items-center gap-2 ${color} text-xs font-medium`}
        data-testid="sync-status-badge"
        title={
          !status.online
            ? `Hors ligne — ${status.pending} en attente`
            : status.pending > 0
              ? `${status.pending} en attente de synchronisation`
              : 'Synchronisé'
        }
      >
        <Icon size={14} className={status.isSyncing ? 'animate-spin' : ''} />
        {status.pending > 0 && <span>{status.pending}</span>}
        {!status.online && <span>Hors ligne</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <div>
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                  <Icon size={16} className={`${status.isSyncing ? 'animate-spin' : ''} ${color.split(' ')[1]}`} />
                  Synchronisation
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {status.online ? 'Connecté' : 'Hors ligne'} — {status.pending} en attente
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="sync-modal-close">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {items.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">
                  Aucune action en attente. Tout est synchronisé.
                </p>
              )}
              {items.map((it) => (
                <div key={it.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-slate-300">
                      {it.method} <span className="text-slate-500">{it.url}</span>
                    </span>
                    {it.status === 'pending' && (
                      <span className="text-amber-400">En attente</span>
                    )}
                    {it.status === 'syncing' && (
                      <span className="text-blue-400 flex items-center gap-1"><RefreshCw size={10} className="animate-spin" /> Sync</span>
                    )}
                    {it.status === 'failed' && (
                      <span className="text-red-400">Échec</span>
                    )}
                  </div>
                  {it.last_error && (
                    <p className="text-red-300 text-[11px] mt-1">{it.last_error}</p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-slate-500">
                      {it.attempts || 0} tentative{(it.attempts || 0) > 1 ? 's' : ''} — {new Date(it.created_at).toLocaleTimeString()}
                    </span>
                    <div className="flex gap-1">
                      {it.status === 'failed' && (
                        <button
                          onClick={() => retryMutation(it.id)}
                          className="text-blue-400 hover:text-blue-300 px-2 py-0.5 rounded text-[11px] border border-blue-500/30"
                          data-testid={`sync-retry-${it.id}`}
                        >
                          Réessayer
                        </button>
                      )}
                      <button
                        onClick={async () => { await deleteMutation(it.id); setItems(await listMutations()); }}
                        className="text-slate-400 hover:text-red-400 px-2 py-0.5 rounded text-[11px] border border-slate-700"
                        title="Supprimer cette action de la file"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-slate-800 flex gap-2">
              <button
                onClick={runSync}
                disabled={!status.online || status.isSyncing}
                className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2"
                data-testid="sync-now-btn"
              >
                <RefreshCw size={14} className={status.isSyncing ? 'animate-spin' : ''} />
                Synchroniser maintenant
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SyncStatusBadge;
